import React, { useCallback, useState } from "react";
import useReveal from "@/hooks/useReveal";
import { SERVICES, U } from "@/data/services";
import { insertRow, uploadFile, notifyForm } from "@/lib/forms";
import { useSettings } from "@/lib/SettingsContext";

const HERO_BG = U("1493225457124-a3eb161ffa5f", 2400, 88);

const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED = /\.(pdf|ppt|pptx|doc|docx|xls|xlsx|jpe?g|png|zip)$/i;

interface FormState {
  firstName: string; lastName: string; email: string; phone: string; company: string;
  eventType: string; budget: string; eventDate: string; venue: string;
  services: Set<string>; details: string; files: File[];
}
const BLANK: FormState = {
  firstName: "", lastName: "", email: "", phone: "", company: "",
  eventType: "", budget: "", eventDate: "", venue: "",
  services: new Set<string>(), details: "", files: [],
};

const Quote: React.FC = () => {
  useReveal();
  const { settings } = useSettings();
  const [form, setForm] = useState<FormState>({ ...BLANK, services: new Set<string>() });
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const toggleSvc = (slug: string) => {
    const next = new Set(form.services);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setForm({ ...form, services: next });
  };

  const addFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const accepted: File[] = []; const rejected: string[] = [];
    Array.from(files).forEach(f => {
      if (f.size > MAX_BYTES) { rejected.push(`${f.name} (over 20MB)`); return; }
      if (!ALLOWED.test(f.name)) { rejected.push(`${f.name} (unsupported type)`); return; }
      accepted.push(f);
    });
    setForm(prev => ({ ...prev, files: [...prev.files, ...accepted] }));
    if (rejected.length) {
      setResult({ ok: false, msg: `Some files were skipped: ${rejected.join(", ")}` });
    }
  }, []);

  const removeFile = (idx: number) => setForm(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== idx) }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setResult(null);
    try {
      const uploaded: { name: string; url: string; size: number }[] = [];
      for (const f of form.files) {
        const u = await uploadFile("uploads", "quotes", f);
        uploaded.push({ name: f.name, url: u.url, size: f.size });
      }
      await insertRow("quote_requests", {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        company: form.company,
        event_type: form.eventType,
        budget: form.budget,
        event_date: form.eventDate || null,
        venue: form.venue,
        services_required: Array.from(form.services),
        additional_details: form.details,
        files: uploaded,
      });
      // Fire-and-forget email notification — never blocks the form.
      console.log("[Quote] submission saved, calling notifyForm…");
      notifyForm({
        kind: "quote",
        to: settings.notification_email,
        data: {
          first_name: form.firstName, last_name: form.lastName,
          email: form.email, phone: form.phone, company: form.company,
          event_type: form.eventType, event_date: form.eventDate || null,
          venue: form.venue, budget: form.budget,
          services_required: Array.from(form.services),
          additional_details: form.details,
          files: uploaded,
        },
      }).then(() => console.log("[Quote] notifyForm resolved")).catch(err => console.error("[Quote] notifyForm failed:", err));
      setResult({ ok: true, msg: "Thank you — your quote request has been received. We'll be in touch within 24 hours." });
      setForm({ ...BLANK, services: new Set<string>() });
    } catch (err: any) {
      setResult({ ok: false, msg: err?.message || "Something went wrong. Please email info@ideationav.com." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="iav-page" data-testid="quote-page">
      <section className="iav-page-hero iav-hero-bg iav-hero-center" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner iav-hero-inner-center">
          <span className="iav-tag-line" data-reveal>Let's Build Something Remarkable</span>
          <h1 data-reveal>GET A<br /><span className="iav-accent">QUOTE.</span></h1>
          <p className="lead" data-reveal>Share the brief and our production team will respond within 24 hours with a tailored proposal.</p>
        </div>
      </section>

      <div className="iav-quote-wrap">
        <form className="iav-form iav-quote-form" onSubmit={onSubmit} data-testid="quote-form">
          <div className="iav-form-row">
            <div className="iav-field"><label>First Name *</label><input required className="iav-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} /></div>
            <div className="iav-field"><label>Last Name *</label><input required className="iav-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} /></div>
          </div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Email *</label><input required type="email" className="iav-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="iav-field"><label>Phone *</label><input required type="tel" className="iav-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="iav-field"><label>Company</label><input className="iav-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Event Type *</label>
              <select required className="iav-select" value={form.eventType} onChange={e => setForm({ ...form, eventType: e.target.value })}>
                <option value="">Select…</option>
                <option>Corporate Event</option>
                <option>Live Concert</option>
                <option>Government &amp; Gala</option>
                <option>Exhibition</option>
                <option>Social Event</option>
                <option>Other</option>
              </select>
            </div>
            <div className="iav-field"><label>Budget</label>
              <select className="iav-select" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })}>
                <option value="">Select…</option>
                <option>Under AED 10,000</option>
                <option>AED 10,000 – 50,000</option>
                <option>AED 50,000 – 150,000</option>
                <option>AED 150,000 – 500,000</option>
                <option>AED 500,000+</option>
              </select>
            </div>
          </div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Event Date</label><input type="date" className="iav-input" value={form.eventDate} onChange={e => setForm({ ...form, eventDate: e.target.value })} /></div>
            <div className="iav-field"><label>Venue / Location</label><input className="iav-input" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} /></div>
          </div>
          <div className="iav-field">
            <label>Services Required</label>
            <div className="iav-svc-tiles" data-testid="quote-services">
              {SERVICES.map(s => (
                <label key={s.slug} className={`iav-svc-tile ${form.services.has(s.slug) ? "is-on" : ""}`} data-testid={`quote-svc-${s.slug}`}>
                  <input type="checkbox" checked={form.services.has(s.slug)} onChange={() => toggleSvc(s.slug)} />
                  <span className="iav-svc-tile-name">{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="iav-field"><label>Additional Details</label><textarea className="iav-textarea" placeholder="Tell us about your vision, scale, run sheet or any specifics…" value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} /></div>
          <div className="iav-field">
            <label>Attachments (max 20MB each)</label>
            <label className={`iav-file ${drag ? "is-drag" : ""}`}
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
              data-testid="quote-dropzone"
            >
              <input type="file" multiple accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip" onChange={e => addFiles(e.target.files)} />
              <div>Drag &amp; drop files here, or click to browse</div>
              <small>PDF · PPT · DOC · XLS · JPG · PNG · ZIP — up to 20MB each</small>
            </label>
            {form.files.length > 0 && (
              <div className="iav-file-list">
                {form.files.map((f, i) => (
                  <span key={i} className="iav-file-chip">
                    {f.name} <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`} style={{ background: "transparent", border: 0, color: "#fff", cursor: "pointer", marginLeft: 4 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {result && <div className={`iav-form-msg ${result.ok ? "is-ok" : "is-err"}`} data-testid="quote-result">{result.msg}</div>}
          <button type="submit" disabled={submitting} className="iav-btn iav-btn-primary" data-testid="quote-submit">{submitting ? "Submitting…" : "Request Quote"}</button>
        </form>
      </div>
    </main>
  );
};
export default Quote;
